import React, { useEffect, useMemo, useState } from 'react';
import { formatExeclCommon, handleDownload } from '@/pages/dashboard/utils/utils';
import MultiProgressCard from '../../SalesOverview/components/MultiProgressCard';
import { cloneDeep } from 'lodash-es';
import { getLocale } from 'umi';
import { getName, selectValByCondition } from '@home/utils/common';
import { resolveBySegmentType } from '../utils/resolveBySegmentType';

const formatPercentage = (percentage: string, value: string): string => {
  const pct = parseFloat(percentage);
  const val = parseFloat(value);
  if (pct === 0 && val !== 0 && !isNaN(val)) {
    return '<0.1%';
  }
  return percentage;
};


const SalesOverviewTopChartCo = (props: any) => {
  const { data, title, firstHeader, config, defaultSortedVal = 'net_units', setSortTopMetric, sortValue: propsSortValue, secTitle, apiData, segmentVal } = props;
  const [sortValue, setSortValue] = useState(defaultSortedVal || 'net_units');
  // const [sortOrder, setSortOrder] = useState('desc');
  const isEN = getLocale() === 'en-US';
  const metricsInfoSignature = JSON.stringify(apiData?.metricsInfo || {});
  const handleSort = (value: string) => {
    setSortTopMetric?.(value);
    setSortValue(value);
    // setSortOrder(order);
  };

  useEffect(() => {
    if (propsSortValue) {
      setSortValue(propsSortValue);
    }
  }, [propsSortValue]);

  const dataList = useMemo(() => {
    const selected = !sortValue ? data[0] || {} : data?.find((item: { title: string; }) => item.title === sortValue) || {};
    const list = selected?.list || [];
    let selectedList = [...list];
    const newList = cloneDeep(data);
    const returnList: any = {};

    const isLoading = data?.some(item => item.isLoading);
    const isError = data?.some(item => item.isError);

    returnList.isLoading = isLoading;
    returnList.isError = isError;
    returnList.isNoData = selectedList.length === 0;
    if (list.length === 0) {
      return returnList;
    }
    // 和振和沟通后，当前只有desc倒序排，而且子组件也没有支持sortOrder ，可以先注释掉,删掉sortOrder 相关
    selectedList.sort((a, b) =>
      // if (sortOrder === 'asc') 
      //   return parseFloat(a.value) - parseFloat(b.value);
      parseFloat(b.value) - parseFloat(a.value)
    );

    // //把others派在最后
    const others = selectedList.find(item => item?.full_name?.toLowerCase() === 'others');
    if (others) {
      selectedList = selectedList.filter((item) => item?.full_name?.toLowerCase() !== 'others');
      selectedList.push(others);
    }

    selectedList = selectedList.map((item: any) => ({
      ...item,
      displayPercentage: formatPercentage(item.percentage, item.value),
    }));

    const extList = selectedList.map((item: any) => ({
      ...item,
      num: item.num_ext1,
      percentage: item.percentage_ext1,
      value: item.value_ext1,
      displayPercentage: formatPercentage(item.percentage_ext1, item.value_ext1),
    }));

    if (sortValue === defaultSortedVal) {
      newList[0].list = selectedList;
      newList[1].list = extList;
    } else {
      newList[1].list = selectedList;
      newList[0].list = extList;
    }

    returnList.data = newList;
    return returnList;
  }, [data, defaultSortedVal, sortValue]);
  const productList = useMemo(() => {
    const productArray: any[] = [];
    const data = dataList?.data || [];
    for (const element of data) {

      if (element.isError || element.isLoading || element.isNoData) {
        return null;
      };

      let combinedItem = {};
      element.list.forEach((unit: { name: string; num: string; percentage: string; value: string; }) => {
        const pct = formatPercentage(unit.percentage, unit.value);
        const existingItem = productArray.findIndex((each) => each.name === unit.name);

        if (existingItem !== -1) {
          productArray[existingItem].bubble[element.listHeader] = `${unit.num} (${pct})`;
        } else {
          combinedItem = {
            [firstHeader]: `${unit.name}`,
            [element.listHeader]: `${unit.num} (${pct})`,
          };
          productArray.push({
            name: unit.name,
            bubble: combinedItem
          });
        }
      });
    }

    return productArray;
  }, [dataList, firstHeader]);

  const downloadOptions = useMemo(() => {
    const opt = config?.download?.map((val: { key: string; align: string; header: string; headerCn: string; }) => {
      // 支持按 Product / Product Category 动态表头，并兼容 {{metricsInfo.xxx.en}} 模板
      const headerEn = resolveBySegmentType(val.header, segmentVal, apiData, val.key || '');
      const headerCn = resolveBySegmentType(val.headerCn, segmentVal, apiData, val.key || '');
      return {
        key: val.key,
        align: val.align || '',
        // eslint-disable-next-line no-nested-ternary
        header: headerEn === 'HEADER' ? firstHeader : getName(isEN, headerEn, headerCn)
      };
    });
    return opt;
  }, [config, firstHeader, isEN, segmentVal, metricsInfoSignature]);

  const download = useMemo(() => {
    const download = [];
    const data = dataList?.data || [];
    for (const dataItem of data) {
      const isNumber = dataItem?.format === 'number' || dataItem?.format === 'thousandsInit';
      const list = dataItem?.list || [];
      for (const listItem of list) {
        let fullName = listItem?.full_name;
        fullName = fullName?.toLowerCase() === 'others' ? selectValByCondition(isEN, listItem?.full_name, '其他') : listItem?.full_name;
        const existingItem = download.findIndex((each) => each.full_name === fullName);
        const pct = listItem?.percentage !== null && listItem?.percentage !== undefined
          ? formatPercentage(listItem.percentage, listItem.value ?? '0')
          : listItem?.percentage;
        if (existingItem !== -1) {
          download[existingItem][dataItem?.title] = formatExeclCommon(listItem?.value, isNumber);
          download[existingItem][`${dataItem?.title}_percentage`] = formatExeclCommon(pct, isNumber);
        } else {
          download.push({
            full_name: fullName,
            [dataItem?.title]: formatExeclCommon(listItem?.value, isNumber),
            [`${dataItem?.title}_percentage`]: formatExeclCommon(pct, isNumber)
          });
        }
      }
    }
    return download;
  }, [dataList, isEN]);
  const downloadDataFunc = () => {
    handleDownload({
      option: downloadOptions,
      data: download
    }, title, false);
  };

  return (
    <>
      <MultiProgressCard data={dataList} handelDownload={downloadDataFunc}
        handleSort={handleSort} title={title} sortValue={sortValue} item={productList}
        header={firstHeader} secTitle={secTitle} />
    </>
  );
};

export default SalesOverviewTopChartCo;
